import {
  AlertTriangle,
  BadgeCheck,
  BriefcaseIcon,
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  Euro,
  FilePen,
  FileText,
  FileUp,
  Globe,
  Hash,
  Home,
  Mail,
  MapPin,
  Maximize2,
  PartyPopper,
  Phone,
  Shield,
  ShieldAlert,
  ShieldCheck,
  TruckIcon,
  User,
  Users,
} from 'lucide-react';
import type {
  ConditionalVisibility,
  FieldShortcut,
  FormSectionsWithShortcuts,
  KorintModulesProductsConfig,
} from 'korint-sdk/react';
import { ContactKey } from '@folio/utils/customerDisplayName';
import {
  customerControllerEditCustomer,
  CustomFieldDto,
  policyControllerPushPolicyEvent,
} from 'korint-sdk';
import { InvoicesScheduleModule, QuoteFunnelModule } from '@folio/modules';
import { TUTASSUR_RECAP_SHORT_LABELS } from './common';

const showWhenCompanyBeingCreated = [
  {
    fieldName: 'isCompanyBeingCreated',
    condition: 'equals',
    value: 'true',
  },
] as const satisfies ConditionalVisibility[];

const signatorySections: FormSectionsWithShortcuts = [
  {
    title: 'Signataire',
    fields: [
      { name: 'contactCivility', icons: { MONSIEUR: User, MADAME: User, MADEMOISELLE: User } },
      {
        name: 'contactFirstName',
        type: 'text',
        required: true,
        isCustomField: true,
        icon: User,
        colSpan: 6 as const,
      },
      {
        name: 'contactLastName',
        type: 'text',
        required: true,
        isCustomField: true,
        icon: User,
        colSpan: 6 as const,
      },
      {
        name: 'contactAddress',
        type: 'address',
        required: true,
        isCustomField: true,
        icon: MapPin,
      },
      {
        name: 'contactPostCode',
        type: 'postcode',
        countries: ['FR'],
        required: true,
        isCustomField: true,
        icon: Hash,
        colSpan: 4 as const,
      },
      {
        name: 'contactCity',
        type: 'city',
        required: true,
        isCustomField: true,
        icon: Building2,
        colSpan: 8 as const,
      },
      {
        name: 'contactEmail',
        type: 'email',
        required: true,
        isCustomField: true,
        icon: Mail,
        colSpan: 6 as const,
      },
      {
        name: 'contactPhoneNumber',
        type: 'text',
        required: true,
        isCustomField: true,
        icon: Phone,
        colSpan: 6 as const,
      },
      {
        name: 'contactBirthDate',
        type: 'date',
        required: true,
        isCustomField: true,
        icon: CalendarDays,
        colSpan: 6 as const,
      },
      {
        name: 'contactBirthCity',
        type: 'city',
        addressGroup: 'contactBirth',
        required: true,
        isCustomField: true,
        icon: Building2,
        colSpan: 6 as const,
      },
      {
        name: 'contactBirthCountry',
        type: 'country',
        addressGroup: 'contactBirth',
        required: true,
        isCustomField: true,
        icon: Globe,
      },
    ],
  },
];

const financialCompanySections: FormSectionsWithShortcuts = [
  {
    title: "Informations financières de l'année dernière ou projection",
    fields: [
      { name: 'lastExerciseOrForecastRevenue', icon: Euro, colSpan: 6 as const },
      { name: 'yearOfRevenue', icon: CalendarDays, colSpan: 6 as const },
    ],
  },
];

const companySections: FormSectionsWithShortcuts = [
  {
    title: 'Entreprise',
    fields: [
      {
        name: 'isCompanyBeingCreated',
        required: false,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Building2,
        isCustomField: true,
        apiDataAccessor: apiData =>
          apiData.customFields?.isCompanyBeingCreated ? 'true' : 'false',
        formDataAccessor: formData => formData.isCompanyBeingCreated === 'true',
      },
      {
        name: 'companySiret',
        type: 'company',
        required: true,
        isCustomField: true,
        displayCompanyInformation: true,
        icon: Building2,
        // Consider making it more generic, this should be reusable.
        apiDataAccessor: apiData =>
          apiData.customFields?.companySiret
            ? {
                siret: apiData?.customFields?.companySiret,
                name: apiData?.customFields?.companyName,
                location: {
                  postCode: apiData?.customFields?.companyHeadquarterPostcode,
                },
                nafLabel: apiData?.customFields?.companyNafLabel,
              }
            : null,
        formDataAccessor: 'companySiret.siret',
        showWhen: [
          {
            fieldName: 'isCompanyBeingCreated',
            condition: 'equals',
            value: 'false',
          },
        ],
      },
      {
        name: 'companyName',
        required: true,
        type: 'text',
        icon: Building2,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyHeadquarterAddress',
        required: true,
        type: 'address',
        icon: MapPin,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyHeadquarterPostcode',
        required: true,
        type: 'postcode',
        countries: ['FR'],
        icon: Hash,
        colSpan: 4 as const,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyHeadquarterCity',
        required: true,
        type: 'city',
        icon: Building2,
        colSpan: 8 as const,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyLegalStatus1',
        required: true,
        type: 'text',
        icon: FileText,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyCreationDate',
        required: true,
        type: 'date',
        icon: CalendarDays,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyActivity',
        required: true,
        type: 'text',
        icon: BriefcaseIcon,
        isCustomField: true,
      },
    ],
  },
  ...financialCompanySections,
];

const beneficialOwnersSections: FormSectionsWithShortcuts = [
  {
    title: 'Nombre de bénéficiaires effectifs',
    fields: [
      {
        name: 'numberOfBeneficialOwners',
        type: 'number' as const,
        display: 'cards' as const,
        required: true,
        isCustomField: true,
        min: 0,
        max: 5,
        icon: Users,
        ignoreInPayload: true,
        apiDataAccessor: customer =>
          Object.entries(customer.customFields ?? {}).filter(
            ([key, value]) => key.startsWith('beneficialOwnerFirstName') && Boolean(value),
          ).length,
      },
    ],
  },
  ...Array.from({ length: 5 }, (_, index) => ({
    title: `Bénéficiaire effectif ${index + 1}`,
    fields: [
      {
        name: `beneficialOwnerFirstName${index + 1}`,
        type: 'text' as const,
        required: true,
        clearable: true,
        isCustomField: true,
        icon: User,
        colSpan: 6 as const,
        DEPRECATED_placeholder: 'Michel',
      },
      {
        name: `beneficialOwnerLastName${index + 1}`,
        type: 'text' as const,
        required: true,
        clearable: true,
        isCustomField: true,
        icon: User,
        colSpan: 6 as const,
        DEPRECATED_placeholder: 'Dupont',
      },
      {
        name: `beneficialOwnerDateOfBirth${index + 1}`,
        type: 'date' as const,
        required: true,
        clearable: true,
        isCustomField: true,
        icon: CalendarDays,
      },
      {
        name: `beneficialOwnerBirthCity${index + 1}`,
        type: 'city' as const,
        addressGroup: `beneficialOwnerBirth${index + 1}`,
        required: true,
        clearable: true,
        isCustomField: true,
        icon: Building2,
        colSpan: 6 as const,
        DEPRECATED_placeholder: 'Paris',
      },
      {
        name: `beneficialOwnerBirthCountry${index + 1}`,
        type: 'country' as const,
        addressGroup: `beneficialOwnerBirth${index + 1}`,
        required: true,
        clearable: true,
        isCustomField: true,
        icon: Globe,
        colSpan: 6 as const,
        DEPRECATED_placeholder: 'France',
      },
    ],
    showWhen: [
      {
        fieldName: 'numberOfBeneficialOwners',
        condition: 'greaterThanOrEquals' as const,
        value: index + 1,
      },
    ],
  })),
];

const questionsSections: FormSectionsWithShortcuts = [
  {
    title: 'Informations complémentaires',
    fields: [
      {
        name: 'specificQuestionHelvetia1',
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: ShieldAlert,
        isCustomField: true,
        apiDataAccessor: ({ customFields }) =>
          customFields?.specificQuestionHelvetia1 ? 'true' : 'false',
        formDataAccessor: ({ specificQuestionHelvetia1 }) => specificQuestionHelvetia1 === 'true',
      },
      {
        name: 'specificQuestionHelvetia2',
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Shield,
        isCustomField: true,
        apiDataAccessor: ({ customFields }) =>
          customFields?.specificQuestionHelvetia2 ? 'true' : 'false',
        formDataAccessor: ({ specificQuestionHelvetia2 }) => specificQuestionHelvetia2 === 'true',
      },
      {
        name: 'specificQuestionHelvetia3',
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Euro,
        isCustomField: true,
        apiDataAccessor: ({ customFields }) =>
          customFields?.specificQuestionHelvetia3 ? 'true' : 'false',
        formDataAccessor: ({ specificQuestionHelvetia3 }) => specificQuestionHelvetia3 === 'true',
      },
      {
        name: 'specificQuestionHelvetia4',
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Globe,
        isCustomField: true,
        apiDataAccessor: ({ customFields }) =>
          customFields?.specificQuestionHelvetia4 ? 'true' : 'false',
        formDataAccessor: ({ specificQuestionHelvetia4 }) => specificQuestionHelvetia4 === 'true',
      },
      {
        name: 'specificQuestionHelvetia5',
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: ShieldCheck,
        isCustomField: true,
        apiDataAccessor: ({ customFields }) =>
          customFields?.specificQuestionHelvetia5 ? 'true' : 'false',
        formDataAccessor: ({ specificQuestionHelvetia5 }) => specificQuestionHelvetia5 === 'true',
      },
      {
        name: 'specificQuestionHelvetia6',
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: BriefcaseIcon,
        isCustomField: true,
        apiDataAccessor: ({ customFields }) =>
          customFields?.specificQuestionHelvetia6 ? 'true' : 'false',
        formDataAccessor: ({ specificQuestionHelvetia6 }) => specificQuestionHelvetia6 === 'true',
      },
    ],
  },
  {
    title: 'Informations supplémentaires',
    fields: [
      {
        name: 'insuredCapital',
        type: 'select',
        required: true,
        isCustomField: true,
        options: [
          { label: '100 000 €', value: '100_000_€' },
          { label: '150 000 €', value: '150_000_€' },
        ],
        icons: {
          '100_000_€': Shield,
          '150_000_€': ShieldCheck,
        },
        perRow: 2,
      },
      {
        name: 'territoriality',
        type: 'select',
        required: true,
        isCustomField: true,
        options: [
          { label: 'France', value: 'FRANCE' },
          { label: 'Union Européenne + Suisse et Norvège', value: 'EU_SWITZERLAND_NORWAY' },
        ],
        icons: {
          FRANCE: MapPin,
          EU_SWITZERLAND_NORWAY: Globe,
        },
        perRow: 2,
      },
    ],
  },
];

const assetFields: FieldShortcut[] = [
  {
    name: 'startedAt',
    required: true,
    type: 'datetime',
    min: 'now',
  },
];

const BILLING_FREQUENCY_ICONS = {
  MONTHLY: CalendarDays,
  QUARTERLY: CalendarRange,
  HALF_YEARLY: CalendarClock,
  YEARLY: CalendarCheck,
};

const quotePolicySections: FormSectionsWithShortcuts = [
  {
    title: 'Formule',
    fields: [
      {
        name: 'excessMaterialDamage',
        type: 'select',
        required: false,
        isCustomField: true,
        options: [
          { label: '250 €', value: '250_€' },
          { label: '500 €', value: '500_€' },
        ],
        icons: {
          '250_€': Shield,
          '500_€': ShieldAlert,
        },
        perRow: 2,
      },
      {
        name: 'excessIntangibleDamage',
        type: 'select',
        required: false,
        isCustomField: true,
        options: [{ label: '750 €', value: '750_€' }],
        icon: Shield,
      },
    ],
  },
  {
    title: 'Options',
    fields: [
      {
        name: 'optionCCC',
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        required: false,
        isCustomField: true,
        icon: Shield,
        apiDataAccessor: apiData => (apiData.customFields?.optionCCC ? 'true' : 'false'),
        formDataAccessor: formData => formData.optionCCC === 'true',
      },
      {
        name: 'optionDDC',
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        required: false,
        isCustomField: true,
        icon: Euro,
        apiDataAccessor: apiData => (apiData.customFields?.optionDDC ? 'true' : 'false'),
        formDataAccessor: formData => formData.optionDDC === 'true',
      },
      {
        name: 'optionDLO',
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        required: false,
        isCustomField: true,
        icon: Shield,
        apiDataAccessor: apiData => (apiData.customFields?.optionDLO ? 'true' : 'false'),
        formDataAccessor: formData => formData.optionDLO === 'true',
      },
      {
        name: 'optionMBE',
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        required: false,
        isCustomField: true,
        icon: Shield,
        apiDataAccessor: apiData => (apiData.customFields?.optionMBE ? 'true' : 'false'),
        formDataAccessor: formData => formData.optionMBE === 'true',
      },
      {
        name: 'optionMTR',
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        required: false,
        isCustomField: true,
        icon: Shield,
        apiDataAccessor: apiData => (apiData.customFields?.optionMTR ? 'true' : 'false'),
        formDataAccessor: formData => formData.optionMTR === 'true',
      },
      {
        name: 'optionPDF',
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        required: false,
        isCustomField: true,
        icon: Shield,
        apiDataAccessor: apiData => (apiData.customFields?.optionPDF ? 'true' : 'false'),
        formDataAccessor: formData => formData.optionPDF === 'true',
      },
      {
        name: 'optionTAV',
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        required: false,
        isCustomField: true,
        icon: AlertTriangle,
        apiDataAccessor: apiData => (apiData.customFields?.optionTAV ? 'true' : 'false'),
        formDataAccessor: formData => formData.optionTAV === 'true',
      },
      {
        name: 'optionTCI',
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        required: false,
        isCustomField: true,
        icon: Shield,
        apiDataAccessor: apiData => (apiData.customFields?.optionTCI ? 'true' : 'false'),
        formDataAccessor: formData => formData.optionTCI === 'true',
      },
      {
        name: 'optionTNT',
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        required: false,
        isCustomField: true,
        icon: AlertTriangle,
        apiDataAccessor: apiData => (apiData.customFields?.optionTNT ? 'true' : 'false'),
        formDataAccessor: formData => formData.optionTNT === 'true',
      },
      {
        name: 'optionTTD',
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        required: false,
        isCustomField: true,
        icon: Shield,
        apiDataAccessor: apiData => (apiData.customFields?.optionTTD ? 'true' : 'false'),
        formDataAccessor: formData => formData.optionTTD === 'true',
      },
      {
        name: 'optionT2R',
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        required: false,
        isCustomField: true,
        icon: Shield,
        apiDataAccessor: apiData => (apiData.customFields?.optionT2R ? 'true' : 'false'),
        formDataAccessor: formData => formData.optionT2R === 'true',
      },
      {
        name: 'optionPropertyDommage',
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        required: false,
        isCustomField: true,
        icon: Home,
        apiDataAccessor: apiData =>
          apiData.customFields?.optionPropertyDommage ? 'true' : 'false',
        formDataAccessor: formData => formData.optionPropertyDommage === 'true',
      },
    ],
  },
  {
    // TODO: clear if optionPropertyDommage is unchecked
    title: 'Option: Dommages aux biens',
    showWhen: [
      {
        fieldName: 'optionPropertyDommage',
        condition: 'equals',
        value: 'true',
      },
    ],
    fields: [
      {
        name: 'propertyArea',
        description: "Jusqu'à 1500 m²",
        type: 'text',
        required: true,
        isCustomField: true,
        icon: Maximize2,
      },
      {
        name: 'propertyInsuredCapital',
        description: "Jusqu'à 300 000 €",
        type: 'text',
        required: true,
        isCustomField: true,
        icon: Euro,
      },
      {
        name: 'propertyAddress',
        type: 'address',
        required: true,
        isCustomField: true,
        icon: MapPin,
      },
      {
        name: 'propertyPostCode',
        type: 'postcode',
        countries: ['FR'],
        required: true,
        isCustomField: true,
        icon: Hash,
        colSpan: 4 as const,
      },
      {
        name: 'propertyCity',
        type: 'city',
        required: true,
        isCustomField: true,
        icon: Building2,
        colSpan: 8 as const,
      },
    ],
  },
];

const manualComponentsGroups = [
  {
    id: 'premium',
    componentIds: ['PREMIUM', 'WHOLESALE_COMMISSION', 'INSURANCE_TAX'],
    placeholders: {
      PREMIUM: '1000',
      WHOLESALE_COMMISSION: '100',
      INSURANCE_TAX: '150',
    } as Record<string, string>,
  },
  {
    id: 'wholesaleFees',
    componentIds: ['WHOLESALE_ADDITIONAL_COMMISSION', 'WHOLESALE_SUBSCRIPTION_FEE'],
    placeholders: {
      WHOLESALE_ADDITIONAL_COMMISSION: '10',
      WHOLESALE_SUBSCRIPTION_FEE: '50',
    } as Record<string, string>,
  },
  {
    id: 'retailFees',
    componentIds: ['RETAIL_COMMISSION', 'RETAIL_SUBSCRIPTION_FEE', 'RETAIL_MANAGEMENT_FEE'],
    placeholders: {
      RETAIL_COMMISSION: '10',
      RETAIL_SUBSCRIPTION_FEE: '50',
      RETAIL_MANAGEMENT_FEE: '30',
    } as Record<string, string>,
  },
  {
    id: 'otherFees',
    componentIds: ['RENEWAL_FEE'],
    placeholders: {
      RENEWAL_FEE: '30',
    } as Record<string, string>,
  },
];

const productCustomPath = '/transport-marchandises-helvetia';

const policyHolderInvitationRole = ['DRIVER'];

export const tutassurTransportMarchandisesHelvetiaConfig = {
  display: {
    category: 'transport',
    icon: TruckIcon,
    badges: ['custom'],
  },
  productCustomPath,
  discoveryFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'documents-upload',
        type: 'documentUploadFunnel' as const,
        context: 'policy' as const,
        icon: FileUp,
      },
      {
        // Needed so an asset exists for the quote step, so we can fill the startedAt field
        // Needs to be first to avoid issues when clicking "previous" from customer step
        path: 'asset',
        type: 'emptyAssetFunnel',
        hidden: true,
      },
      {
        path: 'company',
        type: 'customerFunnel',
        sections: companySections,
        icon: Building2,
      },
      {
        path: 'customer',
        type: 'customerFunnel',
        sections: signatorySections,
        icon: User,
      },
      {
        path: 'compliance',
        type: 'complianceFunnel',
        icon: BadgeCheck,
      },
      {
        path: 'questions',
        type: 'policyFunnel',
        sections: questionsSections,
        icon: ShieldAlert,
      },
      {
        path: 'billing',
        type: 'billingSelectionFunnel',
        icon: CalendarClock,
        frequencyIcons: BILLING_FREQUENCY_ICONS,
      },
      {
        path: 'quote',
        type: 'custom',
        icon: Shield,
        component: (
          <QuoteFunnelModule
            assetFields={assetFields}
            policySections={quotePolicySections}
            nonDelegatedDocuments={['QUOTE']}
            manualComponentsGroups={manualComponentsGroups}
            recapLabelOverrides={TUTASSUR_RECAP_SHORT_LABELS}
          />
        ),
      },
      {
        path: 'invoicing-schedule',
        type: 'module',
        icon: CalendarDays,
        module: <InvoicesScheduleModule />,
      },
      {
        path: 'beneficial-owners',
        type: 'customerFunnel',
        sections: beneficialOwnersSections,
        icon: Users,
        afterSubmit: async (data, { customerId, branchId }) => {
          const numberOfBeneficialOwners = Number(data.numberOfBeneficialOwners);

          const fieldsToUpdate: CustomFieldDto[] = Array.from(
            { length: 5 - numberOfBeneficialOwners },
            (_, index) => [
              `beneficialOwnerFirstName${numberOfBeneficialOwners + index + 1}`,
              `beneficialOwnerLastName${numberOfBeneficialOwners + index + 1}`,
              `beneficialOwnerDateOfBirth${numberOfBeneficialOwners + index + 1}`,
              `beneficialOwnerBirthCity${numberOfBeneficialOwners + index + 1}`,
              `beneficialOwnerBirthCountry${numberOfBeneficialOwners + index + 1}`,
            ],
          )
            .flat()
            .map(key => ({ key, value: '' }));

          if (fieldsToUpdate.length > 0) {
            await customerControllerEditCustomer(
              customerId,
              { customFields: fieldsToUpdate },
              { branchId },
            );
          }
        },
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'new-business-end',
        icon: PartyPopper,
        additionalComponents: {
          isQuoteTierModuleVisible: false,
          finishWithoutSendingButton: { enabled: true },
        },
        callback: {
          callToActionCallback: async ({ policyId, branchId }) => {
            await policyControllerPushPolicyEvent(
              policyId,
              { eventType: 'TUTASSUR_NB_QUOTE_EMITTED' },
              { branchId },
            );
          },
        },
      },
    ],
  },
  mtaFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'new',
        type: 'newMTACreation',
        icon: FilePen,
      },
      {
        path: 'company',
        type: 'customerFunnel',
        sections: companySections,
        icon: Building2,
      },
      {
        path: 'customer',
        type: 'customerFunnel',
        sections: signatorySections,
        icon: User,
      },
      {
        path: 'questions',
        type: 'policyFunnel',
        sections: questionsSections,
        icon: ShieldAlert,
      },
      {
        path: 'quote',
        type: 'custom',
        icon: Shield,
        component: (
          <QuoteFunnelModule
            assetFields={assetFields}
            policySections={quotePolicySections}
            nonDelegatedDocuments={['QUOTE']}
            manualComponentsGroups={manualComponentsGroups}
            recapLabelOverrides={TUTASSUR_RECAP_SHORT_LABELS}
          />
        ),
      },
      {
        path: 'invoicing-schedule',
        type: 'module',
        icon: CalendarDays,
        module: <InvoicesScheduleModule />,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'mta-end',
        icon: PartyPopper,
        additionalComponents: {
          isQuoteTierModuleVisible: false,
          finishWithoutSendingButton: { enabled: false },
        },
        callback: {},
      },
    ],
  },
  policyholderRenewalFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'customer',
        type: 'customerFunnel',
        sections: financialCompanySections,
        icon: User,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'renewal-end',
        icon: PartyPopper,
        additionalComponents: {
          isQuoteTierModuleVisible: false,
          finishWithoutSendingButton: { enabled: false },
        },
        callback: {},
        redirectTo: ({ policyId }) => `/policyholder/${policyId}/information`,
      },
    ],
  },
  renewalFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'customer',
        type: 'customerFunnel',
        sections: financialCompanySections,
        icon: User,
      },
      {
        path: 'quote',
        type: 'custom',
        icon: Shield,
        component: (
          <QuoteFunnelModule
            policySections={quotePolicySections}
            nonDelegatedDocuments={['QUOTE']}
            manualComponentsGroups={manualComponentsGroups}
            recapLabelOverrides={TUTASSUR_RECAP_SHORT_LABELS}
          />
        ),
      },
      {
        path: 'invoicing-schedule',
        type: 'module',
        icon: CalendarDays,
        module: <InvoicesScheduleModule />,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'renewal-end',
        icon: PartyPopper,
        additionalComponents: {
          isQuoteTierModuleVisible: false,
          finishWithoutSendingButton: { enabled: false },
        },
        callback: {},
        redirectTo: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}`,
      },
    ],
  },
  policyDashboardEventsModuleConfig: {
    enableAssetComparison: false,
    quoteDerogationFunnelModule: {
      afterEndPath: '/derogations',
      steps: [
        {
          path: 'quote',
          type: 'custom',
          component: (
            <QuoteFunnelModule
              assetFields={assetFields}
              policySections={quotePolicySections}
              nonDelegatedDocuments={['QUOTE']}
              manualComponentsGroups={manualComponentsGroups}
              recapLabelOverrides={TUTASSUR_RECAP_SHORT_LABELS}
            />
          ),
        },
      ],
    },
  },
  priceBreakdownConfig: {
    groups: [
      {
        lines: [
          { key: 'TOTAL_PREMIUM', variant: 'dimmed' },
          { key: 'TOTAL_PREMIUM_RETAIL', variant: 'dimmed' },
        ],
      },
      { lines: [{ key: 'BROKERAGE_FEES' }, { key: 'BROKERAGE_FEES_RETAIL' }] },
      { lines: [{ key: 'MANAGEMENT_FEE' }] },
      {
        lines: [
          { key: 'SUBSCRIPTION_FEE' },
          { key: 'SUBSCRIPTION_FEE_RETAIL' },
          { key: 'RENEWAL_FEE', tooltip: true },
        ],
      },
    ],
  },
  customerInformations: {
    fieldsConfig: [
      'contactCivility',
      { key: 'contactFirstName', type: 'text', contactKey: ContactKey.FIRST_NAME },
      { key: 'contactLastName', type: 'text', contactKey: ContactKey.LAST_NAME },
      { key: 'contactEmail', type: 'email' },
      { key: 'contactPhoneNumber', type: 'text' },
      { key: 'contactBirthDate', type: 'date' },
      { key: 'contactBirthCity', type: 'text' },
      { key: 'contactBirthCountry', type: 'text' },
      { key: 'lastExerciseOrForecastRevenue', type: 'number' },
      { key: 'yearOfRevenue', type: 'text' },
      { key: 'companyActivity', type: 'text' },
      { key: 'isCompanyBeingCreated', type: 'boolean' },
      { key: 'companySiret', type: 'text' },
      { key: 'companySiren', type: 'text' },
      {
        key: 'companyName',
        type: 'text',
        contactKey: ContactKey.COMPANY_NAME,
        forceReadonly: true,
      },
      { key: 'companyNafCode', type: 'text' },
      { key: 'companyNafLabel', type: 'text', forceReadonly: true },
      { key: 'companyCreationDate', type: 'date', forceReadonly: true },
      { key: 'companyLegalStatus1', type: 'text', forceReadonly: true },
      { key: 'companyLegalStatus2', type: 'text', forceReadonly: true },
      { key: 'companyLegalStatus3', type: 'text', forceReadonly: true },
      { key: 'companyHeadquarterAddress', type: 'text', forceReadonly: true },
      {
        key: 'companyHeadquarterPostcode',
        type: 'postcode',
        countries: ['FR'],
        forceReadonly: true,
      },
      { key: 'companyHeadquarterCity', type: 'text', forceReadonly: true },
      { key: 'companyHeadquarterCountry', type: 'text' },
      ...Array.from({ length: 5 }, (_, index) => index + 1).flatMap(index => [
        `beneficialOwnerFirstName${index}`,
        `beneficialOwnerLastName${index}`,
        `beneficialOwnerBirthCity${index}`,
        `beneficialOwnerBirthCountry${index}`,
      ]),
    ],
  },
  policyInformationCard: {
    title: 'Informations complémentaires',
    fieldsConfig: [
      {
        key: 'specificQuestionHelvetia1',
        type: 'boolean',
      },
      {
        key: 'specificQuestionHelvetia2',
        type: 'boolean',
      },
      {
        key: 'specificQuestionHelvetia3',
        type: 'boolean',
      },
      {
        key: 'specificQuestionHelvetia4',
        type: 'boolean',
      },
      {
        key: 'specificQuestionHelvetia5',
        type: 'boolean',
      },
      {
        key: 'specificQuestionHelvetia6',
        type: 'boolean',
      },
      {
        key: 'insuredCapital',
        type: 'text',
        formatValue: value => {
          const map = {
            '100_000_€': '100 000 €',
            '150_000_€': '150 000 €',
          };

          return map[value as keyof typeof map] ?? value;
        },
      },
      {
        key: 'territoriality',
        type: 'text',
        formatValue: value => {
          const map = {
            FRANCE: 'France',
            EU_SWITZERLAND_NORWAY: 'Union Européenne + Suisse et Norvège',
          };

          return map[value as keyof typeof map] ?? value;
        },
      },
      {
        key: 'excessMaterialDamage',
        type: 'text',
        formatValue: value => {
          const map = {
            '250_€': '250 €',
            '500_€': '500 €',
          };

          return map[value as keyof typeof map] ?? value;
        },
      },
      {
        key: 'excessIntangibleDamage',
        type: 'text',
        formatValue: value => {
          const map = {
            '750_€': '750 €',
          };

          return map[value as keyof typeof map] ?? value;
        },
      },
      {
        key: 'optionCCC',
        type: 'boolean',
      },
      {
        key: 'optionDDC',
        type: 'boolean',
      },
      {
        key: 'optionDLO',
        type: 'boolean',
      },
      {
        key: 'optionMBE',
        type: 'boolean',
      },
      {
        key: 'optionMTR',
        type: 'boolean',
      },
      {
        key: 'optionPDF',
        type: 'boolean',
      },
      {
        key: 'optionTAV',
        type: 'boolean',
      },
      {
        key: 'optionTCI',
        type: 'boolean',
      },
      {
        key: 'optionTNT',
        type: 'boolean',
      },
      {
        key: 'optionTTD',
        type: 'boolean',
      },
      {
        key: 'optionT2R',
        type: 'boolean',
      },
      {
        key: 'optionPropertyDommage',
        type: 'boolean',
      },
      {
        key: 'propertyArea',
        type: 'text',
        hideIfEmpty: true,
      },
      {
        key: 'propertyInsuredCapital',
        type: 'text',
        hideIfEmpty: true,
      },
      {
        key: 'propertyAddress',
        type: 'text',
        hideIfEmpty: true,
      },
      {
        key: 'propertyPostCode',
        type: 'postcode',
        countries: ['FR'],
        hideIfEmpty: true,
      },
      {
        key: 'propertyCity',
        type: 'text',
        hideIfEmpty: true,
      },
    ],
  },
  brokerPolicyDashboardHome: {
    isFinalizeNewBusinessModuleVisible: true,
    isAuthorCardModuleVisible: true,
    isPolicyholderInvitationModuleVisible: true,
    isQuoteTierModuleVisible: false,
    isPremiumModuleVisible: true,
    isPolicyLifecycleSummaryModuleVisible: true,
    isInvoicesBalanceModuleVisible: true,
  },
  finalizeNewBusinessModuleConfig: {
    buttons: [
      {
        id: 'send-via-email',
        actionType: 'signature',
        visual: {
          label: 'Envoi du contrat au client',
          icon: 'mail',
          successMessage: 'Contrat envoyé par email',
        },
        signature: {
          signatureType: 'direct',
          deliveryMode: 'EMAIL',
          otp: {
            enabled: false,
          },
        },
        visibilityConditions: {
          policyStatuses: ['POLICY_QUOTED'],
        },
      },
    ],
    rules: [
      {
        id: 'documents-supporting-validation',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/documents`,
        isValid: ({ policy }) => policy.customFields.allClientDocumentsOk === true,
      },
      {
        id: 'wholesaler-quote-uploaded',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/documents`,
        isValid: ({ documents }) =>
          documents.some(({ type, category }) => type === 'non_delegated' && category === 'QUOTE'),
      },
      {
        id: 'sepa-payment',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/payment-methods`,
        isValid: ({ paymentMethods }) =>
          paymentMethods.some(({ paymentMethodType }) => paymentMethodType === 'sepa_debit'),
      },
    ],
  },
  enableAssetComparison: false,
  quoteTierModuleConfig: {
    goToTiersOnMta: '/mta/transport-marchandises-helvetia/${policy.customer}/${policy.id}/new',
    goToTiersOnNewBusiness:
      '/discovery/transport-marchandises-helvetia/${policy.customer}/${policy.id}/${workingBranch?.id}/quote',
    fieldsConfig: [],
    perilsMapping: {},
    isExternalQuoteEnabled: false,
    isChosenTierDisplayed: false,
    isCompareDisabled: true,
  },
  policyholderInvitationModuleConfig: {
    relationTypes: policyHolderInvitationRole,
  },
} satisfies KorintModulesProductsConfig;
